<template>
  <div
    class="card-wrapper"
    v-if="checkPermission(['chartView_student_status_and_attendance'])"
  >
    <div class="card-top">
      <div class="card-header">
        <div class="card-heading">
          <div class="card-icon">
            <i class="el-icon-date"></i>
          </div>
          <div>
            <p class="card-title">Tình trạng điểm danh</p>
            <p class="card-subtitle">{{ periodLabel }}</p>
            <div class="header-tags">
              <span class="header-tag">Tổng lượt: {{ totalRecords }}</span>
              <span class="header-tag header-tag--soft">{{ activeSummaryLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-row" v-if="loaded && summaryItems.length">
        <div
          v-for="item in summaryItems"
          :key="item.label"
          class="summary-card"
          :style="{ '--summary-accent': item.color }"
        >
          <span class="summary-label">{{ item.label }}</span>
          <strong class="summary-value">{{ item.value }}</strong>
        </div>
      </div>
    </div>

    <div class="chart-shell">
      <div class="chart-content" v-if="isLoading">
        <div class="state-card state-card--loading">
          <i class="el-icon-loading state-icon"></i>
          <div>
            <p class="state-title">Đang tải dữ liệu biểu đồ</p>
            <span class="state-text">Hệ thống đang tổng hợp dữ liệu điểm danh theo thời gian.</span>
          </div>
        </div>
      </div>

      <div class="chart-content" v-else-if="loaded && hasData">
        <div class="chartFees">
          <div class="chart-caption">
            <span class="chart-caption__dot"></span>
            <span>Biểu đồ đường theo dõi biến động điểm danh trong khoảng thời gian đã chọn</span>
          </div>
          <div class="chartAttendace">
            <ChartLine
              :chartData="dataConnection.chartData"
              :options="dataConnection.options"
              :height="340"
            ></ChartLine>
          </div>
        </div>
      </div>

      <div class="chart-content chart-empty" v-else>
        <div class="state-card state-card--empty">
          <i class="el-icon-document-delete state-icon"></i>
          <div>
            <p class="state-title">Chưa có dữ liệu điểm danh</p>
            <span class="state-text">Khi có bản ghi điểm danh trong khoảng thời gian này, biểu đồ sẽ hiển thị tại đây.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import checkPermission from "@/utils/permission";
import moment from "moment";
import ChartStatisticalService from "@/services/ChartStatisticalService";
import ChartLine from "@/views/chartStatistical/chart/ChartLine.vue";

export default {
  components: { ChartLine },
  name: "AttendanceStatusChart",
  data() {
    return {
      dataConnection: {},
      loaded: false,
      isLoading: false,
      dataLabels: [],
      dataList1: [],
      dataList2: [],
      dataList3: [],
      dataList4: [],
      dataSearch: {
        type: "date",
        dateList: [
          moment().subtract(29, "days").format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ],
      },
    };
  },
  computed: {
    periodLabel() {
      const fromDate = moment(String(this.dataSearch.dateList[0])).format("DD/MM/YYYY");
      const toDate = moment(String(this.dataSearch.dateList[1])).format("DD/MM/YYYY");
      return `${fromDate} - ${toDate}`;
    },
    hasData() {
      return this.dataLabels.length > 0;
    },
    totalRecords() {
      const combineList = [this.dataList1, this.dataList2, this.dataList3, this.dataList4];
      return combineList.reduce((sum, list) => {
        const listTotal = list.reduce((acc, item) => acc + Number(item || 0), 0);
        return sum + listTotal;
      }, 0);
    },
    summaryItems() {
      return [
        { label: "Đi học", color: "#4f8dfd", value: this.sumByList(this.dataList1) },
        { label: "Nghỉ có phép", color: "#ffb547", value: this.sumByList(this.dataList2) },
        { label: "Nghỉ không phép", color: "#ff6b6b", value: this.sumByList(this.dataList3) },
        { label: "Chưa điểm danh", color: "#7c8798", value: this.sumByList(this.dataList4) },
      ];
    },
    activeSummaryLabel() {
      if (!this.summaryItems.length || this.totalRecords === 0) {
        return "Chưa có dữ liệu";
      }

      const topItem = [...this.summaryItems].sort((leftItem, rightItem) => rightItem.value - leftItem.value)[0];
      return topItem && topItem.label ? `Nổi bật: ${topItem.label}` : "Chưa có dữ liệu";
    },
  },
  async mounted() {
    await this.searchByPropertiesAttendance();
  },
  methods: {
    checkPermission,
    sumByList(list) {
      return list.reduce((sum, item) => sum + Number(item || 0), 0);
    },
    createLineDataset(label, data, color) {
      return {
        label,
        data,
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: color,
        pointBorderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 3,
        lineTension: 0.28,
        fill: false,
      };
    },
    createLineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 24,
            right: 12,
            left: 12,
            bottom: 8,
          },
        },
        title: {
          display: false,
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            boxWidth: 9,
            padding: 18,
            fontSize: 12,
            fontColor: "#516074",
          },
        },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(19, 31, 53, 0.92)",
          titleFontSize: 12,
          bodyFontSize: 12,
          xPadding: 12,
          yPadding: 12,
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || "";
              const value = Number(tooltipItem.value || 0);
              return `${label}: ${value}`;
            },
          },
        },
        hover: {
          mode: "nearest",
          intersect: false,
        },
        plugins: {
          zoom: {
            zoom: {
              enabled: true,
              mode: "x",
              speed: 1,
              modifierKey: "ctrl",
            },
            pan: {
              enabled: true,
              mode: "x",
            },
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                fontColor: "#6b7a90",
                fontSize: 12,
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 10,
              },
            },
          ],
          yAxes: [
            {
              display: true,
              afterDataLimits(scale) {
                const maxValue = Number(scale.max || 0);
                scale.max = maxValue > 0 ? Math.ceil(maxValue * 1.12 + 1) : 5;
              },
              gridLines: {
                color: "rgba(142, 169, 204, 0.18)",
                zeroLineColor: "rgba(142, 169, 204, 0.24)",
                drawBorder: false,
              },
              ticks: {
                beginAtZero: true,
                precision: 0,
                fontColor: "#7d8ca1",
                fontSize: 12,
                padding: 10,
              },
            },
          ],
        },
      };
    },
    fillData() {
      this.dataConnection = {
        chartData: {
          labels: this.dataLabels,
          datasets: [
            this.createLineDataset("Đi học", this.dataList1, "#4f8dfd"),
            this.createLineDataset("Nghỉ có phép", this.dataList2, "#ffb547"),
            this.createLineDataset("Nghỉ không phép", this.dataList3, "#ff6b6b"),
            this.createLineDataset("Chưa điểm danh", this.dataList4, "#7c8798"),
          ],
        },
        options: this.createLineOptions(),
      };
    },
    async searchByPropertiesAttendance() {
      this.isLoading = true;
      try {
        const resp = await ChartStatisticalService.findAttendanceKids(
          "",
          "",
          this.dataSearch.type,
          this.dataSearch.dateList,
          "",
          "",
          "",
        );
        const results = Array.isArray(resp.data.data) ? resp.data.data : [];
        const dataLabels = [];
        const attendance = [];
        const attendanceYes = [];
        const attendanceNo = [];
        const attendanceUn = [];

        for (let i = 0; i < results.length; i++) {
          const e = results[i];
          dataLabels.push(e.name);
          attendance.push(e.attendance);
          attendanceYes.push(e.attendanceYes);
          attendanceNo.push(e.attendanceNo);
          attendanceUn.push(e.attendanceUn);
        }

        this.dataLabels = dataLabels;
        this.dataList1 = attendance;
        this.dataList2 = attendanceYes;
        this.dataList3 = attendanceNo;
        this.dataList4 = attendanceUn;
        this.fillData();
        this.loaded = true;
      } catch (e) {
        this.$message({
          message: "Failed to get the data",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-top {
  display: flex;
  flex-direction: column;
}

.card-wrapper {
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(31, 45, 61, 0.08);
  padding: 20px;
  margin: 20px 0;
  transition: all 0.3s ease;
  height: 730px;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(122, 147, 184, 0.12);
}

.card-wrapper:hover {
  box-shadow: 0 22px 48px rgba(31, 45, 61, 0.12);
  transform: translateY(-4px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.card-heading {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.card-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ffffff;
  background: linear-gradient(135deg, #4f8dfd, #36cfc9);
  box-shadow: 0 14px 24px rgba(79, 141, 253, 0.24);
}

.card-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #22324d;
}

.card-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #7f8da3;
}

.header-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.header-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #31527f;
  background: rgba(79, 141, 253, 0.12);
}

.header-tag--soft {
  background: rgba(54, 207, 201, 0.12);
  color: #1d7f7b;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-card {
  position: relative;
  padding: 16px 18px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(118, 153, 214, 0.14);
  box-shadow: 0 10px 24px rgba(31, 45, 61, 0.06);
  overflow: hidden;
}

.summary-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--summary-accent);
}

.summary-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #7d8ca1;
  margin-bottom: 8px;
}

.summary-value {
  font-size: 24px;
  line-height: 1;
  color: #21314c;
}

.chart-shell {
  position: relative;
  flex: 1;
  min-height: 390px;
  padding: 18px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, rgba(79, 141, 253, 0.12), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid rgba(118, 153, 214, 0.14);
}

.chart-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.chartFees {
  margin-top: 6px;
  height: 100%;
}

.chartAttendace {
  width: 100%;
}

.chart-caption {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 14px;
  border-radius: 999px;
  background: rgba(79, 141, 253, 0.08);
  color: #4f6483;
  font-size: 13px;
  font-weight: 600;
}

.chart-caption__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4f8dfd;
  box-shadow: 0 0 0 4px rgba(79, 141, 253, 0.14);
}

.state-card {
  min-height: 240px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: left;
}

.state-card--loading {
  background: linear-gradient(135deg, rgba(79, 141, 253, 0.08), rgba(54, 207, 201, 0.08));
}

.state-card--empty {
  background: linear-gradient(135deg, rgba(242, 247, 255, 0.9), rgba(248, 251, 255, 0.95));
}

.state-icon {
  font-size: 36px;
  color: #4f8dfd;
}

.state-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #26344c;
}

.state-text {
  font-size: 13px;
  color: #7d8ca1;
  line-height: 1.6;
}

@media (max-width: 1200px) {
  .card-wrapper {
    min-height: auto;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .chart-shell {
    padding: 14px;
  }

  .state-card,
  .card-header {
    flex-direction: column;
    text-align: center;
  }
}
</style>
